import { useState } from "react";

import { useToast } from "@/app/providers/ToastProvider";
import { FormErrorSummary } from "@/components/common/FormErrorSummary";
import { FormField } from "@/components/common/FormField";
import { LoadingOverlay } from "@/components/common/LoadingOverlay";
import { NumericStepper } from "@/components/common/NumericStepper";
import { unitLabel, UnitPicker } from "@/components/common/UnitPicker";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NAFA_FORM_WIDTH } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { FALLBACK_CATEGORY_CODE } from "@/features/categories/api/categoriesApi";
import { useCreateProvision, useUpdateProvision } from "@/features/provisions/hooks/useProvisions";
import type { Provision } from "@/features/provisions/types";
import { getApiValidationErrors } from "@/lib/apiClient";
import { focusFirstField } from "@/lib/forms";
import { toErrorMessage } from "@/lib/swr";

type ProvisionFieldErrors = {
  name?: string;
  precision?: string;
};

const PROVISION_FIELD_IDS = {
  name: "provision-name",
  category: "provision-category",
  description: "provision-description",
  precision: "provision-precision",
} as const;

/** Creation and edition share every field, so they share one form. */
export default function ProvisionFormPage({ provision, onBack }: { provision?: Provision; onBack: () => void }) {
  const isEditing = Boolean(provision);

  const [typeCode] = useState(provision?.typeCode ?? FALLBACK_CATEGORY_CODE);
  const [name, setName] = useState(provision?.name ?? "");
  const [description, setDescription] = useState(provision?.description ?? "");
  const [unit, setUnit] = useState(provision?.measurementUnit ?? "Kg");
  const [precision, setPrecision] = useState(String(provision?.measurementPrecision ?? "1"));
  const [fieldErrors, setFieldErrors] = useState<ProvisionFieldErrors>({});
  const [formError, setFormError] = useState<string>();

  const { create, isCreating } = useCreateProvision();
  const { update, isUpdating } = useUpdateProvision();
  const toast = useToast();

  const isSaving = isCreating || isUpdating;

  const clearFieldError = (field: keyof ProvisionFieldErrors) => {
    setFieldErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    setFormError(undefined);
  };

  const validate = () => {
    const errors: ProvisionFieldErrors = {};
    if (!name.trim()) errors.name = "Donnez un nom à la matière.";
    if (!(parseFloat(precision) > 0)) errors.precision = "Indiquez une quantité supérieure à 0.";
    setFieldErrors(errors);
    setFormError(Object.keys(errors).length > 0 ? "Corrigez les champs signalés avant d’enregistrer." : undefined);
    focusFirstField(Object.keys(errors).map((field) => PROVISION_FIELD_IDS[field as keyof typeof PROVISION_FIELD_IDS]));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(undefined);
    if (!validate()) return;

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      measurementUnit: unit,
      measurementPrecision: parseFloat(precision),
      typeCode,
    };

    try {
      if (provision) {
        await update(provision.id, payload);
        toast.success(`« ${payload.name} » mise à jour.`);
      } else {
        await create(payload);
        toast.success(`« ${payload.name} » ajoutée aux matières premières.`);
      }
      onBack();
    } catch (e) {
      // The form keeps its values so nothing is lost.
      const apiErrors = getApiValidationErrors(e);
      const nextErrors: ProvisionFieldErrors = {
        name: apiErrors.name,
        precision: apiErrors.measurementPrecision,
      };
      const fieldErrorIds = Object.entries(nextErrors)
        .filter(([, message]) => Boolean(message))
        .map(([field]) => PROVISION_FIELD_IDS[field as keyof typeof PROVISION_FIELD_IDS]);
      setFieldErrors(nextErrors);
      setFormError(
        fieldErrorIds.length > 0
          ? "Corrigez les champs signalés avant d’enregistrer."
          : (toErrorMessage(e) ?? undefined),
      );
      focusFirstField(fieldErrorIds);
      toast.error(toErrorMessage(e) ?? "La matière n’a pas pu être enregistrée.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`flex ${NAFA_FORM_WIDTH} flex-col gap-6`}>
      {isSaving && <LoadingOverlay message="Enregistrement…" />}

      <div className="flex items-center gap-3">
        <BackButton onClick={onBack} />
        <PageHeader
          title={isEditing ? "Modifier la matière" : "Nouvelle matière première"}
          subtitle={isEditing ? "Les changements valent pour les prochains calculs." : "Quatre informations suffisent."}
        />
      </div>

      <FormErrorSummary
        fields={[
          { id: PROVISION_FIELD_IDS.name, label: "Nom", message: fieldErrors.name },
          { id: PROVISION_FIELD_IDS.precision, label: "Plus petite quantité comptée", message: fieldErrors.precision },
        ]}
      >
        {formError}
      </FormErrorSummary>

      <FormField
        label="Nom"
        htmlFor={PROVISION_FIELD_IDS.name}
        error={fieldErrors.name}
        errorId={`${PROVISION_FIELD_IDS.name}-error`}
      >
        <Input
          id={PROVISION_FIELD_IDS.name}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearFieldError("name");
          }}
          placeholder="ex. Tomate fraîche"
          autoFocus
          aria-invalid={Boolean(fieldErrors.name)}
          aria-describedby={fieldErrors.name ? `${PROVISION_FIELD_IDS.name}-error` : undefined}
        />
      </FormField>

      <fieldset className="min-w-0">
        <legend className="mb-2 text-[15px] font-medium">Unité de mesure</legend>
        <UnitPicker value={unit} onChange={setUnit} />
      </fieldset>

      <FormField
        label="Plus petite quantité comptée"
        hint={"Vous compterez cette matière par tranches de " + (precision || "…") + " " + unitLabel(unit) + "."}
        error={fieldErrors.precision}
        htmlFor={PROVISION_FIELD_IDS.precision}
        errorId={`${PROVISION_FIELD_IDS.precision}-error`}
        hintId={`${PROVISION_FIELD_IDS.precision}-hint`}
      >
        <NumericStepper
          id={PROVISION_FIELD_IDS.precision}
          step={0.01}
          min={0}
          value={precision}
          onChange={(e) => {
            setPrecision(String(e));
            clearFieldError("precision");
          }}
          aria-invalid={Boolean(fieldErrors.precision)}
          aria-describedby={
            fieldErrors.precision
              ? `${PROVISION_FIELD_IDS.precision}-error ${PROVISION_FIELD_IDS.precision}-hint`
              : `${PROVISION_FIELD_IDS.precision}-hint`
          }
        />
      </FormField>

      <FormField
        label="Description"
        htmlFor={PROVISION_FIELD_IDS.description}
        hint="Facultatif."
        hintId={`${PROVISION_FIELD_IDS.description}-hint`}
      >
        <Input
          id={PROVISION_FIELD_IDS.description}
          aria-describedby={`${PROVISION_FIELD_IDS.description}-hint`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Précision utile pour vos équipes"
        />
      </FormField>

      <div className="flex gap-3">
        <Button type="submit" size="lg" disabled={isSaving}>
          {isEditing ? "Enregistrer" : "Ajouter la matière"}
        </Button>
        <Button type="button" variant="outline" size="lg" onClick={onBack}>
          Annuler
        </Button>
      </div>
    </form>
  );
}
